import { useCallback, useEffect, useState } from 'react';
import { Loader2, Play, Printer, FileSpreadsheet, Download } from 'lucide-react';
import type { QuotaPolicy } from '@cca/xero-shared';
import {
  api,
  type QuotaPolicySummary,
  type QuotaRunResponse,
  type ExclusionCategory,
} from '../../api/client';
import {
  PolicyForm,
  printQuotaScenarios,
  downloadScenariosXlsx,
  downloadScenariosCsv,
  type ScenarioForShare,
} from '@cca/quota-ui';
import { defaultPolicy } from './policyDefaults';
import { fmt } from '@cca/ui';
import { HBarChart, PieChart, type ChartRow, type PieSlice } from './QuotaCharts';

// Compare up to THREE quota scenarios at once (engine + /whatif already run one;
// this fans out to three and lays the results beside each other and the current
// quota). Nothing here is saved or stored — it's pure modelling. Each scenario
// has its own bands, category switches and base exclusion.

const THIS_YEAR = new Date().getFullYear();
const SLOT_LABELS = ['A', 'B', 'C'];
const SLOT_COLORS = ['#2f6f4f', '#8a5a2b', '#3a5a8a'];

interface Slot {
  policy: QuotaPolicy;
  year: number;
  result: QuotaRunResponse | null;
}

function freshSlot(label: string): Slot {
  return {
    policy: { ...defaultPolicy(THIS_YEAR), policy_version: `Scenario ${label}` },
    year: THIS_YEAR,
    result: null,
  };
}

export function ScenarioCompareTab() {
  const [slots, setSlots] = useState<Slot[]>(() => SLOT_LABELS.map(freshSlot));
  const [active, setActive] = useState(0);
  const [categories, setCategories] = useState<ExclusionCategory[]>([]);
  const [policies, setPolicies] = useState<QuotaPolicySummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [cats, pols] = await Promise.all([api.quotaCategories(), api.quotaPolicies()]);
      setCategories(cats.filter((c) => c.active));
      setPolicies(pols);
    } catch {
      /* non-fatal */
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const patchSlot = (i: number, patch: Partial<Slot>) =>
    setSlots((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const loadSavedInto = async (i: number, version: string) => {
    if (!version) return;
    try {
      const p = await api.quotaPolicy(version);
      patchSlot(i, { policy: p, year: p.effective_assessment_year ?? THIS_YEAR });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load policy');
    }
  };

  const runAll = async () => {
    setBusy(true);
    setError(null);
    try {
      const results = await Promise.all(slots.map((s) => api.quotaWhatif(s.policy, s.year)));
      setSlots((prev) => prev.map((s, i) => ({ ...s, result: results[i] ?? null })));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'run failed');
    } finally {
      setBusy(false);
    }
  };

  const cur = slots[active] ?? freshSlot(SLOT_LABELS[active] ?? 'A');
  const anyResults = slots.some((s) => s.result);

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
        Model up to three scenarios side by side. Nothing is saved or stored — use Policy &amp; Run
        to commit a version.
      </div>

      {/* Slot selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
        {slots.map((_, i) => (
          <button
            key={i}
            className={`btn btn-sm ${i === active ? '' : 'btn-ghost'}`}
            style={i === active ? { background: SLOT_COLORS[i], borderColor: SLOT_COLORS[i] } : {}}
            onClick={() => setActive(i)}
          >
            Scenario {SLOT_LABELS[i]}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 11, color: 'var(--text2)' }}>
          Start {SLOT_LABELS[active]} from:&nbsp;
          <select
            className="input"
            value=""
            style={{ width: 180 }}
            onChange={(e) => void loadSavedInto(active, e.target.value)}
          >
            <option value="">— blank —</option>
            {policies.map((p) => (
              <option key={p.policy_version} value={p.policy_version}>
                {p.policy_version}
              </option>
            ))}
          </select>
        </label>
        <button className="btn" disabled={busy} onClick={() => void runAll()}>
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Play size={14} />}
          &nbsp;Run all three
        </button>
      </div>

      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 10 }}>{error}</div>}

      {/* Editor for the active scenario */}
      <div
        style={{
          border: `2px solid ${SLOT_COLORS[active]}`,
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: SLOT_COLORS[active] }}>
          Editing Scenario {SLOT_LABELS[active]}
          <span style={{ fontWeight: 400, color: 'var(--text2)' }}> · year {cur.year}</span>
        </div>
        <label style={{ display: 'inline-block', marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text2)', display: 'block' }}>
            Invoice Year
          </span>
          <input
            className="input"
            type="number"
            value={cur.year}
            style={{ width: 100, textAlign: 'right' }}
            onChange={(e) => patchSlot(active, { year: Number(e.target.value) || THIS_YEAR })}
          />
        </label>
        <PolicyForm
          policy={cur.policy}
          onChange={(p) => patchSlot(active, { policy: p })}
          categories={categories}
          hideVersion
        />
      </div>

      {anyResults && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--text2)', marginRight: 'auto' }}>
              Share this comparison
            </span>
            <button
              className="btn btn-ghost btn-sm"
              title="Print / save as PDF"
              onClick={() =>
                printQuotaScenarios({
                  scenarios: forShare(slots),
                  categories,
                  attribution: 'the CCAANZ Finance Portal',
                })
              }
            >
              <Printer size={14} />
              &nbsp;Print / PDF
            </button>
            <button
              className="btn btn-ghost btn-sm"
              title="Download as Excel"
              onClick={() => void downloadScenariosXlsx(forShare(slots), categories)}
            >
              <FileSpreadsheet size={14} />
              &nbsp;Excel
            </button>
            <button
              className="btn btn-ghost btn-sm"
              title="Download as CSV"
              onClick={() => downloadScenariosCsv(forShare(slots), categories)}
            >
              <Download size={14} />
              &nbsp;CSV
            </button>
          </div>
          <ComparisonTable slots={slots} />
        </>
      )}
    </div>
  );
}

function forShare(slots: Slot[]): ScenarioForShare[] {
  return slots.map((s, i) => ({
    label: SLOT_LABELS[i] ?? String(i + 1),
    policy: s.policy,
    year: s.year,
    result: s.result,
  }));
}

function ComparisonTable({ slots }: { slots: Slot[] }) {
  // Row spine: the union of churches across whichever scenarios have run.
  const churchOrder: { church_id: string; name: string; current_quota: number | null }[] = [];
  const seen = new Set<string>();
  for (const s of slots) {
    for (const r of s.result?.results ?? []) {
      if (!seen.has(r.church_id)) {
        seen.add(r.church_id);
        churchOrder.push({
          church_id: r.church_id,
          name: r.name,
          current_quota: r.current_quota,
        });
      }
    }
  }
  const payableOf = (slot: Slot, churchId: string): number | null => {
    const row = slot.result?.results.find((r) => r.church_id === churchId);
    if (!row || !row.computable) return null;
    return row.payable_quota;
  };
  const totalCurrent = churchOrder.reduce((s, c) => s + (c.current_quota ?? 0), 0);

  // Charts — only scenarios that have actually run, churches highest-first.
  const ran = slots.map((slot, i) => ({ slot, i })).filter((x) => x.slot.result);
  const maxPayable = (churchId: string): number =>
    Math.max(0, ...ran.map((x) => payableOf(x.slot, churchId) ?? 0));
  const chartChurches = [...churchOrder].sort(
    (a, b) => maxPayable(b.church_id) - maxPayable(a.church_id),
  );
  const compareSeries = [
    { label: 'Current', color: '#b59a6f' },
    ...ran.map((x) => ({
      label: `Scenario ${SLOT_LABELS[x.i]}`,
      color: SLOT_COLORS[x.i] ?? '#888',
    })),
  ];
  const compareRows: ChartRow[] = chartChurches.map((c) => ({
    name: c.name,
    values: [c.current_quota, ...ran.map((x) => payableOf(x.slot, c.church_id))],
  }));
  const sharePies = ran.map((x) => ({
    label: SLOT_LABELS[x.i] ?? String(x.i + 1),
    slices: chartChurches
      .map((c) => ({ name: c.name, value: payableOf(x.slot, c.church_id) ?? 0 }))
      .filter((s) => s.value > 0) as PieSlice[],
  }));

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr
              style={{
                textAlign: 'right',
                color: 'var(--text2)',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <th style={{ textAlign: 'left', padding: 6 }}>Church</th>
              <th style={{ padding: 6 }}>Current quota</th>
              {slots.map((s, i) => (
                <th key={i} style={{ padding: 6, color: SLOT_COLORS[i] }}>
                  {s.result ? `Scenario ${SLOT_LABELS[i]}` : `${SLOT_LABELS[i]} (not run)`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {churchOrder.map((c) => (
              <tr
                key={c.church_id}
                style={{ borderBottom: '1px solid var(--border)', textAlign: 'right' }}
              >
                <td style={{ textAlign: 'left', padding: 6, fontWeight: 600 }}>{c.name}</td>
                <td style={{ padding: 6 }}>{fmt(c.current_quota)}</td>
                {slots.map((s, i) => {
                  const p = payableOf(s, c.church_id);
                  return (
                    <td key={i} style={{ padding: 6 }}>
                      {s.result ? (p == null ? '—' : fmt(p)) : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr
              style={{ textAlign: 'right', fontWeight: 700, borderTop: '2px solid var(--border)' }}
            >
              <td style={{ textAlign: 'left', padding: 6 }}>Total</td>
              <td style={{ padding: 6 }}>{fmt(totalCurrent)}</td>
              {slots.map((s, i) => (
                <td key={i} style={{ padding: 6, color: SLOT_COLORS[i] }}>
                  {s.result ? fmt(s.result.total_payable) : ''}
                </td>
              ))}
            </tr>
            <tr style={{ textAlign: 'right', fontSize: 11, color: 'var(--text2)' }}>
              <td style={{ textAlign: 'left', padding: 6 }}>vs current quota</td>
              <td style={{ padding: 6 }}>—</td>
              {slots.map((s, i) => {
                if (!s.result) return <td key={i} style={{ padding: 6 }} />;
                const d = s.result.total_payable - totalCurrent;
                return (
                  <td key={i} style={{ padding: 6, color: d > 0 ? 'var(--red)' : 'var(--green)' }}>
                    {d >= 0 ? '+' : '−'}
                    {fmt(Math.abs(d))}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {ran.length > 0 && chartChurches.length > 0 && (
        <>
          <HBarChart
            title="Quota by church, across scenarios"
            note="Current quota beside each scenario that's been run, highest first."
            series={compareSeries}
            rows={compareRows}
            format={(n) => fmt(n)}
          />
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Share of total quota, by church
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {sharePies.map((p) => (
                <PieChart
                  key={p.label}
                  title={`Scenario ${p.label}`}
                  note="Top 10; the rest grouped as “Others”."
                  slices={p.slices}
                  size={180}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
